// Fabric capacity and AI consumption - reads and actions for the console.
// G360_K634
//
// Reads settle to { data, error, missing } where missing = the feature is not
// switched on for this organisation yet (the function or RPC does not exist
// here). Nothing in this file ever puts developer text on screen: a missing
// feature reads "not switched on", a failure reads the server's own plain
// sentence or a short generic one.
import { supabase } from '@govern360/shared';

export const NOTICE_KEY = 'fabric_ai_consumption_2026_10';
/* Pay-as-you-go list price per CU-hour, East US (Azure Retail Prices). Only
   ever used for a figure that says it is an estimate. */
export const PAYG_CU_HOUR_USD = 0.18;

function isMissingFn(error) {
  return !!error && (error.code === 'PGRST202' || error.code === '42883' || /could not find the function/i.test(error.message || ''));
}
function plain(error) {
  const m = (error && (error.message || String(error))) || '';
  if (!m || /PGRST|42883|42P01|relation .* does not exist|migration|stack|syntax error/i.test(m)) return 'The read did not complete. Try again shortly.';
  return m;
}
async function rpc(name, args) {
  try {
    const { data, error } = await supabase.rpc(name, args);
    if (error) return { data: null, error: isMissingFn(error) ? null : plain(error), missing: isMissingFn(error) };
    return { data, error: null, missing: false };
  } catch (e) { return { data: null, error: plain(e), missing: false }; }
}
/* Writes: { ok, data } or { ok: false, missing, error } - never throws. */
async function rpcWrite(name, args) {
  const r = await rpc(name, args);
  if (r.missing) return { ok: false, missing: true, error: 'This is not switched on for this organisation yet.' };
  if (r.error) return { ok: false, error: r.error };
  if (r.data && r.data.ok === false) return { ok: false, error: r.data.error || 'Govern360 refused the change.' };
  return { ok: true, data: r.data };
}

/* A Netlify function call with the caller's session and the organisation on
   screen (x-g360-tenant), like lib/netlifyFn.js, but settling instead of
   throwing so a missing function (404) reads "not switched on". */
async function fn(name, payload, tid, method) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/.netlify/functions/' + name, {
      method: method || 'POST',
      headers: { 'Content-Type': 'application/json', ...(tid ? { 'x-g360-tenant': tid } : {}), Authorization: 'Bearer ' + ((session && session.access_token) || '') },
      body: method === 'GET' ? undefined : JSON.stringify(payload || {}),
    });
    const j = await res.json().catch(() => ({}));
    if (res.status === 404) return { ok: false, missing: true, error: 'This is not switched on for this organisation yet.' };
    if (res.status === 401) return { ok: false, error: 'Your session expired. Sign in again.' };
    if (res.status === 403) return { ok: false, error: j.error || 'Only an owner or admin can do this.' };
    if (!res.ok || j.ok === false) return { ok: false, error: j.error ? String(j.error) : 'The request did not complete (HTTP ' + res.status + ').', data: j };
    return { ok: true, data: j };
  } catch (e) { return { ok: false, error: 'Govern360 could not be reached. Check your connection and try again.' }; }
}

export const readFabricView = (tid, days) => rpc('fabric_view', { p_tenant: tid, p_days: days || 90 });
export const captureBaseline = (tid, workload, capacityId, days, note) =>
  rpcWrite('fabric_capture_baseline', { p_tenant: tid, p_workload: workload, p_capacity: capacityId || null, p_days: days || 28, p_note: note || null });
export const setFabricSettings = (tid, uri, db, table, enabled) =>
  rpcWrite('fabric_set_settings', { p_tenant: tid, p_eventhouse_uri: uri || null, p_db: db || null, p_table: table || null, p_enabled: !!enabled });
export const readNotice = (tid, key) => rpc('console_notice', { p_tenant: tid, p_key: key || NOTICE_KEY });
export const ackNotice = (tid, key, action, snoozeDays) =>
  rpcWrite('console_notice_ack', { p_tenant: tid, p_key: key || NOTICE_KEY, p_action: action, p_snooze_days: snoozeDays == null ? 7 : snoozeDays });

export const runFabricProbe = (tid) => fn('fabric-probe', { tenant_id: tid }, tid);
export const runFabricSync = (tid) => fn('fabric-sync', { tenant_id: tid }, tid);
/* The Govern360 Microsoft app's client id and Entra tenant id - identifiers,
   not secrets - for the setup commands (same read as the Connections page
   Grant panel, kit 605). */
export async function readAppIds(tid) {
  const r = await fn('entra-consent-status', null, tid, 'GET');
  if (!r.ok) return { clientId: null, entraTenantId: null, error: r.error, missing: !!r.missing };
  return { clientId: (r.data && r.data.client_id) || null, entraTenantId: (r.data && r.data.entra_tenant_id) || null };
}

/* G360_K636 - the client id of the organisation's Govern360 Azure connection
   (the app that needs Cost Management Reader), for the one-script setup. The
   connection row is readable by members (cloud_accounts_read); the client id
   is a non-secret field returned by cloud-accounts-fields (owner/admin).
   Settles to { clientId } - null when there is no Azure connection or it
   cannot be read (the script then asks for it). */
// G360_K637 - also says whether the organisation HAS an Azure connection
// (exists: true | false | null when it could not be read).
export async function readAzureAppId(tid) {
  try {
    let q = supabase.from('cloud_accounts').select('id').eq('provider', 'azure').eq('connection_method', 'azure_service_principal');
    if (tid) q = q.eq('organization_id', tid);
    const { data, error } = await q.limit(1);
    if (error) return { clientId: null, exists: null };
    if (!data || !data.length) return { clientId: null, exists: false };
    const r = await fn('cloud-accounts-fields', { id: data[0].id }, tid);
    const f = (r.ok && r.data && r.data.fields) || {};
    return { clientId: typeof f.client_id === 'string' ? f.client_id : null, exists: true };
  } catch (e) { return { clientId: null, exists: null }; }
}

/* G360_K637 - how Fabric data reaches Govern360. fabric_view.status.mode
   (0331) when present; otherwise the same rule applied to the setup check
   steps (a server without 0331): working when the Azure connection lists
   capacities or reads cost, or when the Fabric API works for the app and
   gives it something to read. */
export const FABRIC_MODES = ['working_azure', 'working_fabric', 'working_both', 'not_working', 'not_checked'];
export function fabricStatus(view) {
  const v = view || {};
  if (v.status && FABRIC_MODES.indexOf(v.status.mode) >= 0) {
    return { mode: v.status.mode, extras: Array.isArray(v.status.extras_missing) ? v.status.extras_missing : [], basis: v.status.basis || null };
  }
  const steps = (v.probe && Array.isArray(v.probe.steps)) ? v.probe.steps : [];
  const ok = (k) => steps.some((x) => x && x.key === k && x.state === 'ok');
  const checked = !!(v.probe && v.probe.checked_at);
  const az = ok('azure_capacities') || ok('azure_cost');
  const fab = ok('sp_public_api') && (ok('capacity_role') || ok('admin_api'));
  const mode = az && fab ? 'working_both' : az ? 'working_azure' : fab ? 'working_fabric' : checked ? 'not_working' : 'not_checked';
  const extras = [];
  if (!ok('admin_api')) extras.push('admin_api');
  if (!ok('capacity_role')) extras.push('capacity_role');
  if (steps.some((x) => x && x.key === 'events' && (x.state === 'missing' || x.state === 'unknown'))) extras.push('events');
  return { mode, extras, basis: checked ? 'probe' : null };
}
export const isWorking = (mode) => /^working_/.test(String(mode || ''));
/* One line for the setup summary and the Fabric tab. */
export function fabricModeLine(mode) {
  if (mode === 'working_azure') return 'Working through your Azure connection - nothing to do';
  if (mode === 'working_both') return 'Working through your Azure connection and the Govern360 app - nothing to do';
  if (mode === 'working_fabric') return 'Working through the Govern360 app';
  return 'Not connected yet';
}
/* Where a capacity row was read. */
export function capacitySourceLabel(source) {
  if (source === 'azure' || source === 'azure_cost') return 'from Azure';
  if (source === 'fabric_api') return 'from Fabric';
  return null;
}
/* Opens the Azure connection wizard on the Connections page (G360_K637,
   read by CloudAccountsPage). */
export const CONNECT_AZURE_HASH = '#connections?add=azure';

/* Workload keys from the contract, in Microsoft's phase order. */
export const WORKLOADS = [
  ['ontology_ai', 'Ontology AI'], ['copilot', 'Copilot in Fabric'], ['data_agents', 'Data agents'], ['operations_agents', 'Operations agents'],
  ['ai_functions', 'AI Functions'], ['rti', 'Real-Time Intelligence'], ['other_ai', 'Other AI'],
];
export const workloadLabel = (k) => { const w = WORKLOADS.find((x) => x[0] === k); return w ? w[1] : (k ? String(k) : 'Not reported'); };
export const isTotalRow = (d) => !d.workload || d.workload === 'total' || d.workload === 'all';

/* G360_K638 - Copilot use inside Fabric and Power BI, from the Purview audit
   log rows Govern360 already collects (ms_copilot_interactions, 0284) - no
   setup. Who, when, which Microsoft app, which agent. Uses, never capacity
   units. Settles to { data, error, missing } like every read here. */
export const readFabricCopilotUse = (tid, days) => rpc('fabric_copilot_use', { p_tenant: tid, p_days: days || 30 });
